import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage, useAuth, api, COUNTRIES } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Wrench, MapPin, Loader2, CheckCircle2, Send, Building2, 
  Clock, DollarSign, AlertCircle, ArrowLeft, ArrowRight, Plus, Eye,
  Mail, Phone, User, Calendar, X, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ImageUploadField } from '@/components/ImageUploadField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service Request Form Page
export const ServiceRequestPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [matchesCount, setMatchesCount] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [images, setImages] = useState([]);
  
  const [form, setForm] = useState({
    service_type: '',
    title: '',
    description: '',
    country: '',
    city: '',
    urgency: 'medium',
    budget_type: 'unknown',
    budget_amount: '',
    requester_name: user?.nickname,
    requester_email: user?.email,
    requester_phone: ''
  });

  useEffect(() => {
    fetchServiceTypes();
    if (user) {
      setForm(prev => ({
        ...prev,
        requester_name: user.nickname,
        requester_email: user.email
      }));
    }
  }, [user]);

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get('/workshop-requests/service-types');
      setServiceTypes(res.data.service_types || []);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.service_type || !form.title || !form.description || !form.country || !form.city) {
      toast.error(t('common.fillRequired'));
      return;
    }

    if (!user && !form.requester_email) {
      toast.error(t('serviceRequest.emailRequired'));
      return;
    }

    if (!agreed) {
      toast.error(t('serviceRequest.agreeTerms'));
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...form,
        images: images, // Include uploaded image URLs
        budget_amount: form.budget_amount ? parseFloat(form.budget_amount) : null
      };
      
      // Remove empty guest fields if logged in
      if (user) {
        delete data.requester_email;
        delete data.requester_name;
        delete data.requester_phone;
      }

      const res = await api.post('/workshop-requests/requests', data);
      setMatchesCount(res.data.matches_count || 0);
      setSuccess(true);
      toast.success(t('serviceRequest.submitted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <Nav />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="pt-12 pb-8">
              <CheckCircle2 className="h-20 w-20 text-green-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold mb-3">
                {t('serviceRequest.successTitle')}
              </h1>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {matchesCount > 0 
                  ? t('serviceRequest.successDesc', { count: matchesCount }) || `Your request has been sent to ${matchesCount} matching workshops.`
                  : t('serviceRequest.noMatchesDesc')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {user && (
                  <Link to="/dashboard/requests">
                    <Button data-testid="view-requests-btn">
                      <Eye className="mr-2 h-4 w-4" />
                      {t('serviceRequest.viewMyRequests')}
                    </Button>
                  </Link>
                )}
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSuccess(false);
                    setForm({
                      service_type: '',
                      title: '',
                      description: '',
                      country: '',
                      city: '',
                      urgency: 'medium',
                      budget_type: 'unknown',
                      budget_amount: '',
                      requester_name: user?.nickname,
                      requester_email: user?.email,
                      requester_phone: ''
                    });
                    setAgreed(false);
                  }}
                  data-testid="new-request-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('serviceRequest.sendAnother')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="service-request-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back')}
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Wrench className="h-8 w-8 text-brand" />
              <div>
                <CardTitle>{t('serviceRequest.title')}</CardTitle>
                <CardDescription>
                  {t('serviceRequest.subtitle')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Service Type */}
            <div className="space-y-2">
              <Label>{t('serviceRequest.serviceType')} *</Label>
              <Select 
                value={form.service_type} 
                onValueChange={(v) => setForm({...form, service_type: v})}
              >
                <SelectTrigger data-testid="service-type-select">
                  <SelectValue placeholder={t('serviceRequest.selectService')} />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {(() => {
                        const key = `serviceTypes.${type.id}`;
                        const translated = t(key);
                        return translated !== key ? translated : type.name;
                      })()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label>{t('serviceRequest.titleLabel')} *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                placeholder={t('serviceRequest.titlePlaceholder')}
                data-testid="title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{t('serviceRequest.descriptionLabel')} *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder={t('serviceRequest.descriptionPlaceholder')}
                rows={4}
                data-testid="description-input"
              />
              <p className="text-xs text-muted-foreground">
                {t('serviceRequest.descriptionHint')}
              </p>
            </div>

            {/* Image Upload */}
            <ImageUploadField
              images={images}
              onImagesChange={setImages}
              maxImages={8}
              uploadEndpoint="/api/workshop-requests/upload-image"
            />

            {/* Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jobs.country')} *</Label>
                <Select value={form.country} onValueChange={(v) => setForm({...form, country: v})}>
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder={t('auth.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('serviceRequest.city')} *</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({...form, city: e.target.value})}
                  placeholder={t('serviceRequest.cityPlaceholder')}
                  data-testid="city-input"
                />
              </div>
            </div>

            {/* Urgency */}
            <div className="space-y-3">
              <Label>{t('serviceRequest.urgency')}</Label>
              <RadioGroup 
                value={form.urgency} 
                onValueChange={(v) => setForm({...form, urgency: v})}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="urgency-low" />
                  <Label htmlFor="urgency-low" className="text-green-600 cursor-pointer">
                    {t('serviceRequest.urgencyLow')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="medium" id="urgency-medium" />
                  <Label htmlFor="urgency-medium" className="text-amber-600 cursor-pointer">
                    {t('serviceRequest.urgencyMedium')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="high" id="urgency-high" />
                  <Label htmlFor="urgency-high" className="text-red-600 cursor-pointer">
                    {t('serviceRequest.urgencyHigh')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Budget */}
            <div className="space-y-3">
              <Label>{t('serviceRequest.budget')}</Label>
              <RadioGroup 
                value={form.budget_type} 
                onValueChange={(v) => setForm({...form, budget_type: v})}
                className="flex flex-wrap gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hourly" id="budget-hourly" />
                  <Label htmlFor="budget-hourly" className="cursor-pointer">
                    {t('serviceRequest.budgetHourly')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="fixed" id="budget-fixed" />
                  <Label htmlFor="budget-fixed" className="cursor-pointer">
                    {t('serviceRequest.budgetFixed')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="unknown" id="budget-unknown" />
                  <Label htmlFor="budget-unknown" className="cursor-pointer">
                    {t('serviceRequest.budgetUnknown')}
                  </Label>
                </div>
              </RadioGroup>
              
              {form.budget_type !== 'unknown' && (
                <div className="mt-3">
                  <Label>{t('serviceRequest.budgetAmount')}</Label>
                  <Input
                    type="number"
                    value={form.budget_amount}
                    onChange={(e) => setForm({...form, budget_amount: e.target.value})}
                    placeholder="0.00"
                    className="w-40 mt-1"
                    data-testid="budget-input"
                  />
                </div>
              )}
            </div>

            {/* Guest contact info (only if not logged in) */}
            {!user && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('serviceRequest.contactInfo')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('serviceRequest.yourName')}</Label>
                    <Input
                      value={form.requester_name}
                      onChange={(e) => setForm({...form, requester_name: e.target.value})}
                      placeholder={t('serviceRequest.yourNamePlaceholder')}
                      data-testid="name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('serviceRequest.yourEmail')} *</Label>
                    <Input
                      type="email"
                      value={form.requester_email}
                      onChange={(e) => setForm({...form, requester_email: e.target.value})}
                      placeholder="you@example.com"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('serviceRequest.yourPhone')}</Label>
                  <Input
                    value={form.requester_phone}
                    onChange={(e) => setForm({...form, requester_phone: e.target.value})}
                    placeholder="+358..."
                    data-testid="phone-input"
                  />
                </div>
              </div>
            )}

            {/* Terms agreement */}
            <div className="flex items-start space-x-3 p-4 border rounded-lg">
              <Checkbox 
                id="terms" 
                checked={agreed}
                onCheckedChange={setAgreed}
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                {t('serviceRequest.agreeText')}
              </label>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="w-full"
              size="lg"
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t('serviceRequest.submit')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

// Navigation component
const Nav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to={user.role === 'company' ? '/company/dashboard' : '/worker/dashboard'}>
                  <Button variant="outline" size="sm">{t('nav.dashboard')}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
                  {t('auth.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('auth.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('auth.register')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// My Requests Dashboard Page
export const MyRequestsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchRequests = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await api.get('/workshop-requests/requests/my', { params });
      setRequests(res.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchRequests();
  }, [user, fetchRequests, navigate]);

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'outline', className: 'border-gray-500 text-gray-600', label: t('serviceRequest.statusPending') },
      matched: { variant: 'outline', className: 'border-blue-500 text-blue-600', label: t('serviceRequest.statusMatched') },
      approved: { variant: 'outline', className: 'border-green-500 text-green-600', label: t('serviceRequest.statusApproved') },
      rejected: { variant: 'outline', className: 'border-red-500 text-red-600', label: t('serviceRequest.statusRejected') },
      expired: { variant: 'outline', className: 'border-gray-400 text-gray-500', label: t('serviceRequest.statusExpired') },
      cancelled: { variant: 'outline', className: 'border-gray-400 text-gray-500', label: t('serviceRequest.statusCancelled') }
    };
    const c = config[status] || config.pending;
    return <Badge variant={c.variant} className={c.className}>{c.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="my-requests-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold">{t('serviceRequest.myRequests')}</h1>
            <p className="text-muted-foreground">
              {t('serviceRequest.myRequestsDesc')}
            </p>
          </div>
          <Link to="/services/request">
            <Button data-testid="new-request-btn">
              <Plus className="mr-2 h-4 w-4" />
              {t('serviceRequest.newRequest')}
            </Button>
          </Link>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="pending">{t('serviceRequest.statusPending')}</SelectItem>
              <SelectItem value="matched">{t('serviceRequest.statusMatched')}</SelectItem>
              <SelectItem value="approved">{t('serviceRequest.statusApproved')}</SelectItem>
              <SelectItem value="rejected">{t('serviceRequest.statusRejected')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : requests.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">{t('serviceRequest.noRequests')}</p>
              <p className="text-muted-foreground mb-6">
                {t('serviceRequest.noRequestsDesc')}
              </p>
              <Link to="/services/request">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('serviceRequest.createRequest')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} data-testid={`request-${request.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(request.status)}
                        <span className="text-sm text-muted-foreground">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="font-semibold mb-1">{request.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {request.description}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <Badge variant="secondary">
                          {t(`serviceTypes.${request.service_type}`) || request.service_type}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {request.city}, {request.country}
                        </Badge>
                        <Badge variant="outline">
                          {request.matches_count} {t('serviceRequest.workshops')}
                        </Badge>
                        {request.approved_count > 0 && (
                          <Badge className="bg-green-500">
                            {request.approved_count} {t('serviceRequest.approved')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Link to={`/dashboard/requests/${request.id}`}>
                        <Button variant="outline" size="sm" data-testid={`view-${request.id}`}>
                          <Eye className="mr-2 h-4 w-4" />
                          {t('common.view')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Request Detail Page - shows single request with images and offers
export const RequestDetailPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { requestId } = useParams();
  const navigate = useNavigate();
  
  const [request, setRequest] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchRequestDetail = async () => {
      try {
        const res = await api.get(`/workshop-requests/requests/${requestId}`);
        setRequest(res.data);
        
        // Fetch offers for this request
        try {
          const offersRes = await api.get(`/workshop-requests/requests/${requestId}/offers`);
          setOffers(offersRes.data || []);
        } catch (e) {
          console.log('No offers found');
        }
      } catch (error) {
        toast.error(t('common.error'));
        navigate('/dashboard/requests');
      } finally {
        setLoading(false);
      }
    };

    if (!user) {
      navigate('/login');
      return;
    }
    
    fetchRequestDetail();
  }, [requestId, user, navigate, t]);

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  const prevImage = () => setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);

  const getStatusBadge = (status) => {
    const config = {
      pending: { className: 'border-gray-500 text-gray-600', label: t('serviceRequest.statusPending') || 'Ootel' },
      matched: { className: 'border-blue-500 text-blue-600', label: t('serviceRequest.statusMatched') || 'Vastatud' },
      approved: { className: 'border-green-500 text-green-600', label: t('serviceRequest.statusApproved') || 'Kinnitatud' },
      rejected: { className: 'border-red-500 text-red-600', label: t('serviceRequest.statusRejected') || 'Tagasi lükatud' },
      expired: { className: 'border-gray-400 text-gray-500', label: t('serviceRequest.statusExpired') || 'Aegunud' }
    };
    const c = config[status] || config.pending;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Nav />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-background">
        <Nav />
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p>{t('common.notFound') || 'Päringut ei leitud'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="request-detail-page">
        {/* Back button */}
        <Button variant="ghost" className="mb-4" onClick={() => navigate('/dashboard/requests')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back') || 'Tagasi'}
        </Button>

        {/* Request Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">{request.title}</CardTitle>
              {getStatusBadge(request.status)}
            </div>
            <CardDescription>
              {t(`serviceTypes.${request.service_type}`) || request.service_type} • {request.city}, {request.country}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Description */}
            <div>
              <Label className="text-sm font-medium">{t('serviceRequest.descriptionLabel') || 'Kirjeldus'}</Label>
              <p className="mt-1 text-muted-foreground">{request.description}</p>
            </div>

            {/* Images */}
            {request.images && request.images.length > 0 && (
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  {t('serviceRequest.addImages') || 'Pildid'} ({request.images.length})
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {request.images.map((url, idx) => (
                    <div 
                      key={idx}
                      className="relative group cursor-pointer"
                      onClick={() => openLightbox(request.images, idx)}
                      data-testid={`request-image-${idx}`}
                    >
                      <img 
                        src={url} 
                        alt={`Image ${idx + 1}`}
                        className="h-24 w-24 object-cover rounded-lg border hover:opacity-90 transition shadow-sm"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition rounded-lg">
                        <Eye className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span>{t('common.created') || 'Loodud'}: {new Date(request.created_at).toLocaleDateString()}</span>
              <span>{request.matches_count} {t('serviceRequest.workshops') || 'pakkujat'}</span>
              {request.approved_count > 0 && (
                <span className="text-green-600">{request.approved_count} {t('serviceRequest.approved') || 'kinnitatud'}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Offers Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t('serviceRequest.offers') || 'Pakkumised'} ({offers.length})
          </h2>
          
          {offers.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{t('serviceRequest.noOffers') || 'Pakkumisi veel pole. Ootame pakkujate vastuseid.'}</p>
              </CardContent>
            </Card>
          ) : (
            offers.map((offer, idx) => (
              <Card key={offer.id || idx} data-testid={`offer-${idx}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold">{offer.provider_name || 'Pakkuja'}</h3>
                      <p className="text-sm text-muted-foreground">
                        {new Date(offer.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {offer.price && (
                      <Badge className="bg-green-100 text-green-700">
                        €{offer.price}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-sm mb-3">{offer.message}</p>
                  
                  {/* Offer images */}
                  {offer.images && offer.images.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {offer.images.map((url, imgIdx) => (
                        <div 
                          key={imgIdx}
                          className="relative group cursor-pointer"
                          onClick={() => openLightbox(offer.images, imgIdx)}
                          data-testid={`offer-${idx}-image-${imgIdx}`}
                        >
                          <img 
                            src={url} 
                            alt={`Offer image ${imgIdx + 1}`}
                            className="h-20 w-20 object-cover rounded-lg border hover:opacity-90 transition"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition rounded-lg">
                            <Eye className="h-5 w-5 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button 
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
          
          {lightboxImages.length > 1 && (
            <>
              <button
                className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <button
                className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
              >
                <ArrowRight className="h-6 w-6" />
              </button>
            </>
          )}
          
          <img 
            src={lightboxImages[lightboxIndex]} 
            alt={`Image ${lightboxIndex + 1}`}
            className="max-h-[80vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
            {lightboxIndex + 1} / {lightboxImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestPage;
